/**
 * Voting-location vocabularies, in one place rather than inline per view.
 * Mirrors the CHECK constraints in the migration — a value added there must be
 * added here or it renders as a raw database string.
 */

export const LOCATION_TYPES = [
  { value: 'polling_location', label: 'Election Day polling place' },
  { value: 'early_vote', label: 'Early voting site' },
  { value: 'drop_box', label: '24-hour drop box' },
  { value: 'drop_off', label: 'Staffed drop-off' },
] as const;

export type LocationType = (typeof LOCATION_TYPES)[number]['value'];

/** Short forms for table chips, where the full label is too long. */
export const LOCATION_TYPE_SHORT: Record<string, string> = {
  polling_location: 'Election Day',
  early_vote: 'Early voting',
  drop_box: '24-hour',
  drop_off: 'Staffed',
};

export const locationTypeLabel = (value: string): string =>
  LOCATION_TYPES.find((type) => type.value === value)?.label ?? value;

export const locationTypeShort = (value: string): string =>
  LOCATION_TYPE_SHORT[value] ?? value;

/**
 * Where a row came from, and therefore whether the next state import replaces it.
 * This is the single most important thing an admin needs to read off a row.
 */
export const ORIGINS = [
  {
    value: 'import',
    label: 'From state file',
    help: 'Replaced the next time this election is imported.',
  },
  {
    value: 'projected',
    label: 'Projected',
    help: 'Copied from another cycle as a placeholder. Replaced when a real state file is imported for this election.',
  },
  {
    value: 'manual',
    label: 'Admin',
    help: 'Created or edited here. Kept through every import.',
  },
] as const;

export const originLabel = (value: string): string =>
  ORIGINS.find((origin) => origin.value === value)?.label ?? value;

export const originHelp = (value: string): string =>
  ORIGINS.find((origin) => origin.value === value)?.help ?? '';

/** Chip colouring: admin-owned rows are the ones that behave differently. */
export const originVariant = (value: string): 'success' | 'warning' | 'neutral' =>
  value === 'manual' ? 'success' : value === 'projected' ? 'warning' : 'neutral';

/** Fields the workbook import will overwrite unless an admin has claimed them. */
export const PROTECTABLE_PLACE_FIELDS = [
  'display_name',
  'address_line1',
  'address_line2',
  'city',
  'zip',
  'county_name',
  'county_district_id',
] as const;

export const PLACE_FIELD_LABELS: Record<string, string> = {
  display_name: 'Name',
  address_line1: 'Address',
  address_line2: 'Address line 2',
  city: 'City',
  zip: 'ZIP',
  county_name: 'County',
  county_district_id: 'County district',
  latitude: 'Latitude',
  longitude: 'Longitude',
};
